import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { 
  Plane, 
  Calendar as CalendarIcon, 
  MapPin,
  User,
  Loader2,
  Plus,
  MessageSquare,
  Trash2
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelModePage = () => {
  const { user, golfer } = useAuth();
  const [posts, setPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPost, setNewPost] = useState({
    destination_city: '',
    destination_state: '',
    start_at: null,
    end_at: null,
    message: '',
    visibility: 'members'
  });

  useEffect(() => {
    fetchPosts();
    fetchMyPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/travel-posts`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to fetch travel posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/travel-posts/my`);
      setMyPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to fetch my posts:', error);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.destination_city || !newPost.destination_state || !newPost.start_at || !newPost.end_at) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API_URL}/travel-posts`, {
        ...newPost,
        start_at: newPost.start_at.toISOString(),
        end_at: newPost.end_at.toISOString()
      });
      toast.success('Travel post created!');
      setShowCreateDialog(false);
      setNewPost({
        destination_city: '',
        destination_state: '',
        start_at: null,
        end_at: null,
        message: '',
        visibility: 'members'
      });
      fetchPosts();
      fetchMyPosts();
    } catch (error) {
      toast.error('Failed to create travel post');
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API_URL}/travel-posts/${postId}`);
      toast.success('Post deleted');
      fetchMyPosts();
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="travel-mode-page">
      {/* Safety Disclaimer */}
      {showDisclaimer && (
        <div className="bg-amber-900/30 border border-gold/30 rounded-sm p-4 flex items-start gap-3" data-testid="safety-disclaimer">
          <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-gold text-lg">!</span>
          </div>
          <div className="flex-1">
            <p className="text-cream/90 text-sm font-semibold mb-1">Safety Notice</p>
            <p className="text-cream/70 text-xs leading-relaxed">
              Fairway Alliance connects golfers, but does not vet personal meetups. Play smart, meet in public, and connect at your own risk.
            </p>
          </div>
          <button onClick={() => setShowDisclaimer(false)} className="text-cream/50 hover:text-cream text-sm shrink-0">Dismiss</button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-white mb-2">Away Games</h1>
          <p className="text-cream/70">Find golfers to play with when you're traveling</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-gold" data-testid="create-travel-post-btn">
              <Plus className="w-4 h-4 mr-2" />
              Post Your Trip
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-deep-green border-white/20 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-display">Post a Travel Plan</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-cream/90">Destination City</Label>
                  <Input
                    value={newPost.destination_city}
                    onChange={(e) => setNewPost({ ...newPost, destination_city: e.target.value })}
                    placeholder="Miami"
                    className="bg-white/10 border-white/20 text-white"
                    data-testid="travel-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-cream/90">State</Label>
                  <select
                    value={newPost.destination_state}
                    onChange={(e) => setNewPost({ ...newPost, destination_state: e.target.value })}
                    className="w-full h-10 px-4 rounded-sm bg-white/10 border border-white/20 text-white"
                    data-testid="travel-state-select"
                  >
                    <option value="">Select</option>
                    {states.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-cream/90">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className={cn(
                          "w-full justify-start text-left font-normal bg-white/10 border-white/20 text-white",
                          !newPost.start_at && "text-white/50"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newPost.start_at ? format(newPost.start_at, 'MMM d') : 'Start'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={newPost.start_at}
                        onSelect={(date) => setNewPost({ ...newPost, start_at: date })}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-cream/90">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className={cn(
                          "w-full justify-start text-left font-normal bg-white/10 border-white/20 text-white",
                          !newPost.end_at && "text-white/50"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newPost.end_at ? format(newPost.end_at, 'MMM d') : 'End'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={newPost.end_at}
                        onSelect={(date) => setNewPost({ ...newPost, end_at: date })}
                        disabled={(date) => date < (newPost.start_at || new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-cream/90">Message</Label>
                <Textarea
                  value={newPost.message}
                  onChange={(e) => setNewPost({ ...newPost, message: e.target.value })}
                  placeholder="Looking to play 18 holes on Saturday morning..."
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  rows={3}
                  data-testid="travel-message-input"
                />
              </div>

              <Button 
                onClick={handleCreatePost}
                className="w-full btn-gold"
                disabled={creating}
                data-testid="submit-travel-post"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Plane className="w-4 h-4 mr-2" />
                    Post Trip
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* My Active Posts */}
      {myPosts.length > 0 && (
        <div>
          <h2 className="font-display font-semibold text-lg text-white mb-4">Your Active Trips</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myPosts.map((post) => (
              <Card key={post.post_id} className="bg-gold/10 border-gold/30">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-gold mb-2">
                        <MapPin className="w-4 h-4" />
                        <span className="font-semibold">
                          {post.destination_city}, {post.destination_state}
                        </span>
                      </div>
                      <p className="text-cream/70 text-sm mb-2">
                        {format(new Date(post.start_at), 'MMM d')} - {format(new Date(post.end_at), 'MMM d')}
                      </p>
                      <p className="text-white text-sm">{post.message}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeletePost(post.post_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* All Travel Posts */}
      <div>
        <h2 className="font-display font-semibold text-lg text-white mb-4">Golfers Looking to Play</h2>
        {posts.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center">
              <Plane className="w-12 h-12 text-cream/30 mx-auto mb-4" />
              <p className="text-cream/60">No travel posts yet</p>
              <p className="text-cream/40 text-sm mt-1">Be the first to post your trip!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <Card key={post.post_id} className="card-premium bg-white/5 border-white/10 hover-lift">
                <CardContent className="p-5">
                  {/* Golfer Info */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">
                        {post.golfer?.display_name || 'Anonymous'}
                      </p>
                      <div className="flex items-center gap-2">
                        {post.golfer?.skill_level && (
                          <Badge variant="outline" className="text-xs border-white/20 text-cream/70">
                            {post.golfer.skill_level}
                          </Badge>
                        )}
                        {post.golfer?.handicap && (
                          <span className="text-xs text-cream/50">
                            HCP: {post.golfer.handicap}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Destination */}
                  <div className="flex items-center gap-2 text-gold mb-2">
                    <MapPin className="w-4 h-4" />
                    <span className="font-semibold">
                      {post.destination_city}, {post.destination_state}
                    </span>
                  </div>

                  {/* Dates */}
                  <div className="flex items-center gap-2 text-cream/70 text-sm mb-3">
                    <CalendarIcon className="w-4 h-4" />
                    {format(new Date(post.start_at), 'MMM d')} - {format(new Date(post.end_at), 'MMM d, yyyy')}
                  </div>

                  {/* Message */}
                  {post.message && (
                    <p className="text-white/80 text-sm mb-4 line-clamp-3">
                      "{post.message}"
                    </p>
                  )}

                  {/* Action */}
                  <Button 
                    variant="outline" 
                    className="w-full border-gold/50 text-gold hover:bg-gold hover:text-deep-green"
                    data-testid={`contact-traveler-${post.post_id}`}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Reach Out
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelModePage;
